import { Banner } from '@components/display/banners/Banner';
import { Tabs } from '@components/utility/tabs';
import { APP_PREFIX_PATH } from '@constants/common';
import EOrderApiImage from '@developers/assets/images/eorder-api.png';
import { EOrderAPITabContent } from '@developers/routes/EOrderAPI/EOrderAPITabContent';
import { EOrderApiTabValues } from '@developers/types';
import { useAppDispatch } from '@hooks/generic';
import { Box, Stack, Typography } from '@mui/material';
import { setBreadcrumbs } from '@store/breadcrumbSlice';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

export const EOrderAPI = () => {
    const intl = useIntl();
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useAppDispatch();
    const currentTab = (() => {
        const tab = searchParams.get('tab');

        return tab && Object.values(EOrderApiTabValues).includes(tab as EOrderApiTabValues)
            ? (tab as EOrderApiTabValues)
            : EOrderApiTabValues.PersonalizedData;
    })();

    const handleTabChange = (_event: React.SyntheticEvent, value: EOrderApiTabValues) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('tab', value);
        setSearchParams(nextParams);
    };

    useEffect(() => {
        dispatch(
            setBreadcrumbs([
                {
                    label: intl.formatMessage({ id: 'developers', defaultMessage: 'Developers' }),
                    to: APP_PREFIX_PATH + '/developers',
                    skipParams: true,
                },
                {
                    label: intl.formatMessage({
                        id: 'developers.eorderapi.breadcrumb',
                        defaultMessage: 'E-Order API',
                    }),
                    to: APP_PREFIX_PATH + '/developers/api-eorder/',
                },
            ])
        );
    }, [dispatch, intl]);

    return (
        <>
            <title>
                {intl.formatMessage({
                    id: 'developers.eorderapi.title',
                    defaultMessage: 'Victron Energy Professional - Developers - E-Order API',
                })}
            </title>
            <Box component="main" p="16px">
                <Banner
                    data={{
                        header_title: (
                            <FormattedMessage id="developers.eorderapi.header_title" defaultMessage="E-Order API" />
                        ),
                        header_subtitle: (
                            <FormattedMessage
                                id="developers.eorderapi.header_subtitle"
                                defaultMessage="Always up to date"
                            />
                        ),
                        header_text: (
                            <FormattedMessage
                                id="developers.eorderapi.header_text"
                                defaultMessage="Keep your product information synchronized 24/7, track incoming stock, and streamline your e-commerce operations with our powerful API"
                            />
                        ),
                        button_text: (
                            <FormattedMessage
                                id="developers.eorderapi.button_text"
                                defaultMessage="Start with E-order API"
                            />
                        ),
                        button_link: 'https://eorder.victronenergy.com/api/v1/',
                        background: 'light-blue',
                        image: EOrderApiImage,
                        image_alt: 'E-Order API Banner',
                    }}
                    imgProps={{
                        className: '!object-contain !bg-transparent',
                    }}
                    buttonProps={{
                        target: '_blank',
                    }}
                />
                <Typography fontFamily="museo-sans" fontSize={32} letterSpacing={-1} pt="24px" pb="16px">
                    <FormattedMessage id="developers.eorderapi.available_data" defaultMessage="Available Data" />
                </Typography>
                <Tabs value={currentTab} onChange={handleTabChange} variant="scrollable">
                    <Tabs.Tab
                        value={EOrderApiTabValues.PersonalizedData}
                        label={
                            <FormattedMessage
                                id="developers.eorderapi.personalized_data"
                                defaultMessage="Personalized Data"
                            />
                        }
                    />
                    <Tabs.Tab
                        value={EOrderApiTabValues.ComprehensiveProductInformation}
                        label={
                            <FormattedMessage
                                id="developers.eorderapi.comprehensive_product_information"
                                defaultMessage="Comprehensive Product Information per SKU"
                            />
                        }
                    />
                    <Tabs.Tab
                        value={EOrderApiTabValues.StockAvailabilityUpdates}
                        label={
                            <FormattedMessage
                                id="developers.eorderapi.stock_availability_updates"
                                defaultMessage="Stock Availability Updates"
                            />
                        }
                    />
                </Tabs>
                <Stack direction={{ xs: 'column', lg: 'row' }} pt="30px" spacing="14px">
                    <EOrderAPITabContent tab={currentTab} />
                </Stack>
            </Box>
        </>
    );
};
